/**
 * React Query Hooks for Knowledge Management API
 * 
 * Provides data fetching, mutations, and caching for collections and documents.
 * All queries are scoped to the current tenant.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import apiClient, { ApiResponse, PaginatedResponse, getErrorMessage } from '../lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

// =============================================================================
// Types
// =============================================================================

export type CollectionStatus = 'active' | 'indexing' | 'error' | 'archived';
export type DocumentStatus = 'pending' | 'processing' | 'ready' | 'failed';
export type DocumentType = 'file' | 'url' | 'text' | 'confluence' | 'sharepoint' | 'database';

export interface ProcessingConfig {
  chunking: {
    strategy: 'semantic_llm' | 'semantic_heuristic' | 'fixed';
    min_tokens: number;
    max_tokens: number;
    target_tokens: number;
    overlap_tokens: number;
  };
  embedding: {
    model: string;
    dimensions: number;
  };
  enrichment: {
    enrich_with_llm: boolean;
    extract_questions: boolean;
    auto_classify: boolean;
  };
}

export interface ProcessingConfigUpdate {
  chunking?: ProcessingConfig['chunking'];
  embedding?: ProcessingConfig['embedding'];
  enrichment?: ProcessingConfig['enrichment'];
}

export interface Collection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  knowledge_base_id: string | null;
  source_type?: DocumentType | null;
  mode: 'rag' | 'full_document';
  status: CollectionStatus;
  qdrant_collection_name: string;
  embedding_model: string;
  embedding_dimensions: number;
  chunk_size: number;
  chunk_overlap: number;
  processing_config: ProcessingConfig;
  embedding_locked: boolean;
  reprocessing_status: string | null;
  taxonomy: Record<string, unknown>;
  document_count: number;
  total_chunks: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Document {
  id: string;
  collection_id?: string;
  filename: string;
  relative_path?: string;
  // ISS-026: Original path in the external data source
  // (git repo path, SharePoint URL, S3 key...). Null for manually
  // uploaded documents. UI should prefer this over relative_path
  // when present so users see where the document originated.
  source_path?: string | null;
  content_type: string;
  file_size_bytes: number;
  status: DocumentStatus;
  chunk_count: number;
  error_message?: string | null;
  processed_at?: string | null;
  file_hash?: string | null;
  tags: string[];
  metadata?: Record<string, unknown>;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface FolderNode {
  type: 'folder';
  name: string;
  path: string;
  document_count: number;
  total_size_bytes: number;
}

export interface TreeResponse {
  folders: FolderNode[];
  documents: Document[];
  current_folder: string;
  total_documents: number;
}

export interface CreateCollectionInput {
  name: string;
  description?: string;
  mode?: 'rag' | 'full_document';
  embedding_model?: string;
  chunk_size?: number;
  chunk_overlap?: number;
  taxonomy?: Record<string, unknown>;
  knowledge_base_id?: string;
}

export interface UpdateCollectionInput extends Partial<CreateCollectionInput> {
  status?: CollectionStatus;
}

export interface UploadDocumentInput {
  file: File;
  title?: string;
  taxonomy_tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface CollectionListParams {
  page?: number;
  page_size?: number;
  status?: CollectionStatus;
  search?: string;
  knowledge_base_id?: string;
}

export interface DocumentListParams {
  page?: number;
  page_size?: number;
  status?: DocumentStatus;
  source_type?: DocumentType;
  search?: string;
  folder?: string;
}

// =============================================================================
// Query Keys
// =============================================================================

export const collectionKeys = {
  all: (tenantId: string | undefined) => ['collections', tenantId] as const,
  lists: (tenantId: string | undefined) => [...collectionKeys.all(tenantId), 'list'] as const,
  list: (tenantId: string | undefined, params: CollectionListParams) => [...collectionKeys.lists(tenantId), params] as const,
  details: (tenantId: string | undefined) => [...collectionKeys.all(tenantId), 'detail'] as const,
  detail: (tenantId: string | undefined, id: string) => [...collectionKeys.details(tenantId), id] as const,
  documents: (tenantId: string | undefined, collectionId: string) => [...collectionKeys.detail(tenantId, collectionId), 'documents'] as const,
  documentList: (tenantId: string | undefined, collectionId: string, params: DocumentListParams) => 
    [...collectionKeys.documents(tenantId, collectionId), params] as const,
};

export const documentKeys = {
  all: (tenantId: string | undefined) => ['documents', tenantId] as const,
  detail: (tenantId: string | undefined, id: string) => [...documentKeys.all(tenantId), 'detail', id] as const,
  globalList: (tenantId: string | undefined, params: GlobalDocumentListParams) =>
    [...documentKeys.all(tenantId), 'global', params] as const,
};

export interface GlobalDocumentListParams {
  page?: number;
  page_size?: number;
  status?: DocumentStatus;
  search?: string;
  knowledge_base_id?: string;
  collection_id?: string;
}

export interface GlobalDocumentSummary {
  id: string;
  filename: string;
  relative_path: string;
  source_path: string | null;
  content_type: string;
  file_size_bytes: number;
  status: DocumentStatus;
  chunk_count: number;
  tags: string[];
  created_at: string;
  collection_id: string;
  collection_name: string;
  knowledge_base_id: string | null;
  knowledge_base_name: string | null;
}

// =============================================================================
// Collection API Functions
// =============================================================================

async function fetchCollections(params: CollectionListParams = {}): Promise<PaginatedResponse<Collection>> {
  const { data } = await apiClient.get<PaginatedResponse<Collection>>('/api/v1/knowledge/collections', { params });
  return data;
}

async function fetchCollection(id: string): Promise<Collection> {
  const { data } = await apiClient.get<ApiResponse<Collection>>(`/api/v1/knowledge/collections/${id}`);
  return data.data;
}

async function createCollection(input: CreateCollectionInput): Promise<Collection> {
  const { data } = await apiClient.post<ApiResponse<Collection>>('/api/v1/knowledge/collections', input);
  return data.data;
}

async function updateCollection(id: string, input: UpdateCollectionInput): Promise<Collection> {
  const { data } = await apiClient.patch<ApiResponse<Collection>>(`/api/v1/knowledge/collections/${id}`, input);
  return data.data;
}

async function deleteCollection(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/knowledge/collections/${id}`);
}

async function reindexCollection(id: string): Promise<void> {
  await apiClient.post(`/api/v1/knowledge/collections/${id}/reindex`);
}

async function updateProcessingConfig(id: string, config: ProcessingConfigUpdate): Promise<Collection> {
  const { data } = await apiClient.patch<ApiResponse<Collection>>(
    `/api/v1/knowledge/collections/${id}/processing-config`,
    config
  );
  return data.data;
}

async function reprocessCollection(id: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(
    `/api/v1/knowledge/collections/${id}/reprocess`
  );
  return data;
}

async function reclassifyCollection(id: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(
    `/api/v1/knowledge/collections/${id}/reclassify`
  );
  return data;
}

// =============================================================================
// Document API Functions
// =============================================================================

async function fetchDocuments(
  collectionId: string,
  params: DocumentListParams = {}
): Promise<PaginatedResponse<Document>> {
  const { data } = await apiClient.get<PaginatedResponse<Document>>(
    `/api/v1/knowledge/collections/${collectionId}/documents`,
    { params }
  );
  return data;
}

async function fetchDocument(id: string): Promise<Document> {
  const { data } = await apiClient.get<ApiResponse<Document>>(`/api/v1/knowledge/documents/${id}`);
  return data.data;
}

async function fetchDocumentTree(
  collectionId: string,
  folder: string = ''
): Promise<TreeResponse> {
  const { data } = await apiClient.get<{ data: TreeResponse }>(
    `/api/v1/knowledge/collections/${collectionId}/documents/tree`,
    { params: { folder } }
  );
  return data.data;
}

async function uploadDocument(
  collectionId: string,
  input: UploadDocumentInput
): Promise<Document> {
  const formData = new FormData();
  formData.append('file', input.file);
  
  if (input.title) {
    formData.append('title', input.title);
  }
  if (input.taxonomy_tags) {
    formData.append('taxonomy_tags', JSON.stringify(input.taxonomy_tags));
  }
  if (input.metadata) {
    formData.append('metadata', JSON.stringify(input.metadata));
  }
  
  const { data } = await apiClient.post<ApiResponse<Document>>(
    `/api/v1/knowledge/collections/${collectionId}/documents`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return data.data;
}

async function deleteDocument(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/knowledge/documents/${id}`);
}

async function reprocessDocument(id: string): Promise<Document> {
  const { data } = await apiClient.post<ApiResponse<Document>>(
    `/api/v1/knowledge/documents/${id}/reprocess`
  );
  return data.data;
}

// =============================================================================
// Collection Query Hooks
// =============================================================================

/**
 * List collections with pagination and filtering
 * Only runs when a tenant is selected.
 */
export function useCollections(
  params: CollectionListParams = {},
  options?: Omit<UseQueryOptions<PaginatedResponse<Collection>>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: collectionKeys.list(tenantId, params),
    queryFn: () => fetchCollections(params),
    enabled: !!tenantId, // Only run when tenant is selected
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Get single collection by ID
 * Only runs when a tenant is selected.
 */
export function useCollection(
  id: string,
  options?: Omit<UseQueryOptions<Collection>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: collectionKeys.detail(tenantId, id),
    queryFn: () => fetchCollection(id),
    enabled: !!id && !!tenantId, // Only run when tenant is selected
    staleTime: 60 * 1000,
    ...options,
  });
}

// =============================================================================
// Document Query Hooks
// =============================================================================

/**
 * List documents in a collection
 * Only runs when a tenant is selected.
 */
export function useDocuments(
  collectionId: string,
  params: DocumentListParams = {},
  options?: Omit<UseQueryOptions<PaginatedResponse<Document>>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: collectionKeys.documentList(tenantId, collectionId, params),
    queryFn: () => fetchDocuments(collectionId, params),
    enabled: !!collectionId && !!tenantId, // Only run when tenant is selected
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Get document tree (folders + documents) for a collection folder.
 * Only runs when a tenant is selected.
 */
export function useDocumentTree(
  collectionId: string,
  folder: string = '',
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [...collectionKeys.documents(tenantId, collectionId), 'tree', folder] as const,
    queryFn: () => fetchDocumentTree(collectionId, folder),
    enabled: !!collectionId && !!tenantId,
    staleTime: 30 * 1000,
  });
}

/**
 * List all documents across all accessible collections in the current tenant.
 * Backed by GET /api/v1/knowledge/documents (tenant-scoped).
 */
export function useAllDocuments(params: GlobalDocumentListParams = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: documentKeys.globalList(tenantId, params),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<GlobalDocumentSummary>>(
        '/api/v1/knowledge/documents',
        { params }
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

/**
 * Get single document by ID
 * Only runs when a tenant is selected.
 */
export function useDocument(
  id: string,
  options?: Omit<UseQueryOptions<Document>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: documentKeys.detail(tenantId, id),
    queryFn: () => fetchDocument(id),
    enabled: !!id && !!tenantId, // Only run when tenant is selected
    staleTime: 60 * 1000,
    ...options,
  });
}

// =============================================================================
// Collection Mutation Hooks
// =============================================================================

/**
 * Create a new collection
 */
export function useCreateCollection() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: createCollection,
    onSuccess: (newCollection) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists(tenantId) });
      queryClient.setQueryData(collectionKeys.detail(tenantId, newCollection.id), newCollection);
    },
    onError: (error) => {
      console.error('Failed to create collection:', getErrorMessage(error));
    },
  });
}

/**
 * Update a collection
 */
export function useUpdateCollection() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateCollectionInput & { id: string }) =>
      updateCollection(id, input),
    onSuccess: (updatedCollection) => {
      queryClient.setQueryData(collectionKeys.detail(tenantId, updatedCollection.id), updatedCollection);
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to update collection:', getErrorMessage(error));
    },
  });
}

/**
 * Delete a collection
 */
export function useDeleteCollection() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: deleteCollection,
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: collectionKeys.detail(tenantId, deletedId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to delete collection:', getErrorMessage(error));
    },
  });
}

/**
 * Reindex a collection
 */
export function useReindexCollection() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: reindexCollection,
    onSuccess: (_, collectionId) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(tenantId, collectionId) });
    },
  });
}

/**
 * Update processing configuration for a collection.
 * NOTE: Embedding config cannot be changed if collection has documents.
 */
export function useUpdateProcessingConfig() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: ({ id, config }: { id: string; config: ProcessingConfigUpdate }) =>
      updateProcessingConfig(id, config),
    onSuccess: (updatedCollection) => {
      queryClient.setQueryData(collectionKeys.detail(tenantId, updatedCollection.id), updatedCollection);
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to update processing config:', getErrorMessage(error));
    },
  });
}

/**
 * Trigger full reprocessing of all documents in a collection
 */
export function useReprocessCollection() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: reprocessCollection,
    onSuccess: (_, collectionId) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(tenantId, collectionId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.documents(tenantId, collectionId) });
    },
    onError: (error) => {
      console.error('Failed to trigger reprocessing:', getErrorMessage(error));
    },
  });
}

/**
 * Trigger reclassification of all documents in a collection
 */
export function useReclassifyCollection() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: reclassifyCollection,
    onSuccess: (_, collectionId) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(tenantId, collectionId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.documents(tenantId, collectionId) });
    },
    onError: (error) => {
      console.error('Failed to trigger reclassification:', getErrorMessage(error));
    },
  });
}

// =============================================================================
// Document Mutation Hooks
// =============================================================================

/**
 * Upload a document to a collection
 */
export function useUploadDocument(collectionId: string) {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: (input: UploadDocumentInput) => uploadDocument(collectionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.documents(tenantId, collectionId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(tenantId, collectionId) });
    },
    onError: (error) => {
      console.error('Failed to upload document:', getErrorMessage(error));
    },
  });
}

/**
 * Delete a document
 */
export function useDeleteDocument(collectionId: string) {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: documentKeys.detail(tenantId, deletedId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.documents(tenantId, collectionId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(tenantId, collectionId) });
    },
    onError: (error) => {
      console.error('Failed to delete document:', getErrorMessage(error));
    },
  });
}

/**
 * Reprocess a document
 */
export function useReprocessDocument() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: reprocessDocument,
    onSuccess: (updatedDocument) => {
      queryClient.setQueryData(documentKeys.detail(tenantId, updatedDocument.id), updatedDocument);
    },
  });
}
